import {
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Box } from "@mui/system";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { DelendLoginButton } from "../../components/Buttons";
import {
  DelendLoginField,
  DelendPasswordField,
} from "../../components/TextFields";
import BrandLogo from "../../components/Vectors/BrandLogo";
import DangerIcon from "../../components/Vectors/DangerIcon";
import LoginWave from "../../components/Vectors/LoginWave";
import UsernameIcon from "../../components/Vectors/UsernameIcon";
import { useAppDispatch } from "../../hooks/hooks";
import AuthService from "../../services/AuthService";
import ProfileService from "../../services/ProfileService";
import { isValidCpf, isValidEmail } from "../../services/UtilsService";
import { add } from "../../store/profileSlice";
import MixPanelMetrics from "../../tools/MixPanelMetrics";
import "./Login.css";

const styles = {
  paperStyle: {
    padding: 20,
    overflow: "auto",
    width: "80vw",
    maxWidth: 460,
    margin: "12px auto",
  },

  line: {
    height: 1,
    background: "#ECEDF2",
    marginTop: 12,
  },

  lineText: {
    color: "#8087A6",
    fontWeight: 500,
    lineHeight: "24px",
    fontSize: "16px",
  },

  wrongLoginCombination: {
    color: "#E71D36",
    textAlign: "center",
    fontSize: 12,
    margin: "18px auto",
  },
};

const Divider = () => {
  return (
    <Grid
      paddingY={2}
      container
      display={"flex"}
      justifyContent={"space-between"}
    >
      <Grid item xs={5} style={styles.line} />
      <Grid style={styles.lineText} item>
        ou
      </Grid>
      <Grid item xs={5} style={styles.line} />
    </Grid>
  );
};

const Login = () => {
  let navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [loginFail, setLoginFail] = useState(false);
  const [user, setUser] = useState({
    username: "",
    password: "",
    errors: "",
  });

  const [loading, setLoading] = useState(false);
  const [enableButton, setEnableButton] = useState(false);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUser({
      ...user,
      [event.target.name]: event.target.value,
    });
  };

  const handleSubmit = async (event: React.SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoginFail(false);
    setLoading(true);
    const response = await AuthService.login(user);

    if (response.status === 401) {
      MixPanelMetrics.sendEvent({
        event: "Login",
        eventName: "visualização página de login",
        createdAt: new Date().toISOString(),
        personUuid: "",
        subscriptionUuid: "",
        properties: {
          isSuccess: false,
          username: user.username,
          platform: window.navigator.userAgent,
          error: response.data.message,
        },
      });
      setLoading(false);
      setLoginFail(true);
    }
    if (response.status === 200) {
      MixPanelMetrics.sendEvent({
        event: "Login",
        eventName: "visualização página de login",
        createdAt: new Date().toISOString(),
        personUuid: response.data.personUuid,
        subscriptionUuid: response.data.subscriptionUuid,
        properties: {
          isSuccess: true,
          username: user.username,
          platform: window.navigator.userAgent,
        },
      });
      const profile = await ProfileService.getProfile();
      dispatch(add(profile));
      setLoading(false);
      navigate("/dashboard");
    }
  };

  const ResponsiveLogoBox = styled("div")(({ theme }) => ({
    textAlign: "center",
    [theme.breakpoints.up("md")]: {
      paddingTop: "10vh",
    },
    [theme.breakpoints.down("md")]: {
      paddingTop: "1vh",
    },
  }));

  useEffect(() => {
    setEnableButton(
      user &&
        !!user.password &&
        (isValidEmail(user.username) || isValidCpf(user.username))
    );
  }, [user]);

  return (
    <React.Fragment>
      <LoginWave />
      <Box display={"flex-column"}>
        <ResponsiveLogoBox>
          <BrandLogo />
        </ResponsiveLogoBox>

        <Paper elevation={10} style={styles.paperStyle}>
          <Box>
            <Box display={"flex"} justifyContent={"center"}>
              <h2
                style={{
                  color: "#5E5A81",
                  fontSize: "24px",
                  lineHeight: "32px",
                  fontWeight: "bold",
                }}
              >
                Olá!
              </h2>
            </Box>
            <h4
              style={{
                color: "#8087A6",
                fontSize: "16px",
                lineHeight: "23px",
                fontWeight: 400,
              }}
            >
              Acesse a sua conta com CPF ou e-mail e sua senha
            </h4>
            <form onSubmit={handleSubmit}>
              <Box paddingTop={2}>
                <DelendLoginField
                  label="Usuário"
                  name="username"
                  value={user.username}
                  onChange={handleChange}
                  variant="standard"
                  inputProps={{
                    style: { fontSize: 12, height: 30 },
                    endAdornment: (
                      <InputAdornment
                        sx={{ marginRight: "-5px" }}
                        position="end"
                      >
                        <IconButton
                          sx={{
                            "&:hover": { backgroundColor: "transparent" },
                          }}
                        >
                          <UsernameIcon text={user.username} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Box marginTop={2}>
                  <DelendPasswordField
                    label="Senha"
                    name="password"
                    fullWidth={true}
                    value={user.password}
                    onChange={handleChange}
                  />
                </Box>
              </Box>

              <Typography style={{ marginTop: 6, color: "red" }} align="right">
                <Link className="forgotPassword" to="/forgot-password">
                  Esqueceu sua senha?
                </Link>
              </Typography>

              <Box height={42} />

              <DelendLoginButton disable={!enableButton} loading={loading} />

              <Divider />

              <Grid item xs={12}>
                <Typography align="center">
                  <Link className="createAccount" to="/signup">
                    criar uma conta
                  </Link>
                </Typography>
              </Grid>

              {loginFail && (
                <Box
                  sx={styles.wrongLoginCombination}
                  display={"flex"}
                  justifyContent={"center"}
                >
                  <DangerIcon />
                  <Typography
                    paddingX={0.5}
                    style={{ color: "red", fontSize: "12px" }}
                  >
                    Credenciais inválidas
                  </Typography>
                </Box>
              )}
            </form>
          </Box>
        </Paper>
      </Box>
    </React.Fragment>
  );
};

export default Login;
